'use client';

import { LAYERS } from '@slims/shared/energy';
import type { TelemetryRange } from '@slims/ui/charts';
import { MeterCurveView } from '@slims/ui/organisms';
import { useMemo, useState } from 'react';
import { PodDetailExplode, type PodDetailTarget } from '@/components/energy/metric-explode';
import { useMeterHourly } from '@/hooks/use-meter-hourly';

interface TelemetryPanelProps {
	meterId: string;
	/**
	 * Il punto di connessione del misuratore. Presente = il pannello offre
	 * l'esploso, che mostra il POD per intero — questo grafico è una direzione
	 * sola, e su un prosumer l'altra metà della storia sta nell'altro misuratore.
	 */
	podId?: string | null;
	/** Which energy direction to plot */
	series: 'prelevata' | 'immessa';
	seriesLabel: string;
	accent?: string;
}

const DAY = 86_400_000;

function rangeBounds(range: TelemetryRange, customFrom?: string, customTo?: string) {
	const now = Date.now();
	if (range === 'custom') {
		const from = customFrom
			? new Date(customFrom).toISOString()
			: new Date(now - 7 * DAY).toISOString();
		const to = customTo
			? new Date(`${customTo}T23:59:59`).toISOString()
			: new Date(now).toISOString();
		return { from, to };
	}
	const span = range === 'day' ? DAY : range === 'week' ? 7 * DAY : 30 * DAY;
	return { from: new Date(now - span).toISOString(), to: new Date(now).toISOString() };
}

/** Unica curva riconciliata: la sorgente disponibile viene scelta dal server. */
export function TelemetryPanel({
	meterId,
	podId,
	series,
	seriesLabel,
	accent = '#00e5ff',
}: TelemetryPanelProps) {
	const [range, setRange] = useState<TelemetryRange>('day');
	const [customFrom, setCustomFrom] = useState<string>();
	const [customTo, setCustomTo] = useState<string>();
	const [pod, setPod] = useState<PodDetailTarget | null>(null);

	const { from, to } = useMemo(
		() => rangeBounds(range, customFrom, customTo),
		[range, customFrom, customTo],
	);

	const { data, isLoading, error, refetch } = useMeterHourly({ meterId, from, to });

	const points = useMemo(
		() =>
			data.map((d) => ({
				hour: d.hour,
				value: Math.round(d[series] * 100) / 100,
				sourceLabel: LAYERS.find((layer) => layer.name === d.source)?.label,
			})),
		[data, series],
	);

	return (
		<>
			<MeterCurveView
				data={points}
				accent={accent}
				unit=" kWh"
				seriesLabel={seriesLabel}
				loading={isLoading}
				error={error}
				onRetry={refetch}
				range={range}
				customFrom={customFrom}
				customTo={customTo}
				onRangeChange={(r, opts) => {
					setRange(r);
					if (opts) {
						if ('from' in opts) setCustomFrom(opts.from);
						if ('to' in opts) setCustomTo(opts.to);
					}
				}}
				onExplode={
					podId
						? () => setPod({ podId, day: new Date(Date.now() - DAY).toISOString().slice(0, 10) })
						: undefined
				}
			/>
			<PodDetailExplode target={pod} onClose={() => setPod(null)} />
		</>
	);
}
